import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { ExternalLink, Download, Target } from "lucide-react";

const SOURCE_LABEL = {
  reddit: "Reddit",
  news: "News",
  listicle: "Listicle",
  "first-party": "First-party",
  wiki: "Wiki",
  podcast: "Podcast",
  video: "Video",
  other: "Other",
};

const SOURCE_COLOR = {
  reddit: { bg: "#FFEFEA", fg: "#C84A2F" },
  news: { bg: "#E8F4F0", fg: "#10A37F" },
  listicle: { bg: "#E5F0F2", fg: "#20808D" },
  "first-party": { bg: "var(--aw-accent-soft)", fg: "var(--aw-accent-ink)" },
  wiki: { bg: "#EFEAFA", fg: "#7B5BD9" },
  podcast: { bg: "#FBEBE2", fg: "#D97757" },
  video: { bg: "#E8EFFB", fg: "#4285F4" },
  other: { bg: "var(--aw-surface-2)", fg: "var(--aw-muted)" },
};

function downloadCsv(rows, filename) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Citations() {
  const { current, projects, selectProject } = useProject();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!current) return;
    setLoading(true);
    api
      .get(`/projects/${current.project_id}/citations`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [current]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.sources;
    if (filter === "pitch") return data.pitch_list;
    return data.sources.filter((s) => s.source_type === filter);
  }, [data, filter]);

  const sourceTypes = useMemo(() => {
    if (!data) return [];
    const counts = {};
    data.sources.forEach((s) => {
      counts[s.source_type] = (counts[s.source_type] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const exportCsv = () => {
    const rows = [
      ["Domain", "Frequency", "Authority", "Source type", "Brand mentions", "Engines"],
      ...filtered.map((s) => [
        s.domain,
        s.frequency,
        s.authority,
        SOURCE_LABEL[s.source_type] || s.source_type,
        s.brand_mentions,
        (s.engines || []).join(" / "),
      ]),
    ];
    downloadCsv(rows, `citations-${current?.project_id}-${filter}.csv`);
  };

  if (!current) {
    return (
      <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
        <div className="p-8 text-sm" style={{ color: "var(--aw-muted)" }}>
          No project selected.
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div data-testid="citations-page">
        <div className="mb-8">
          <div className="eyebrow">Sources</div>
          <h1
            className="mt-2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 40,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Citations
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 560 }}>
            Every AI answer cites sources. We rank them by how often each
            domain appears across all engines for your category — so you know
            exactly where to invest in content, PR or partnerships.
          </p>
        </div>

        {data?.is_seed && (
          <div
            className="mb-6 px-4 py-3 rounded-[10px] text-xs flex items-center justify-between"
            style={{ background: "var(--aw-accent-soft)", color: "var(--aw-accent-ink)" }}
            data-testid="citations-seed-banner"
          >
            <span>
              Showing representative data. Run your first scan to populate live
              citations.
            </span>
            <a href="/dashboard/runs" className="underline">
              Run now →
            </a>
          </div>
        )}

        {loading ? (
          <div className="card-aw p-8 text-center text-sm" style={{ color: "var(--aw-muted)" }}>
            Loading citations…
          </div>
        ) : !data ? (
          <div className="card-aw p-8 text-center text-sm" style={{ color: "var(--aw-muted)" }}>
            No data.
          </div>
        ) : (
          <>
            {/* Pitch list callout */}
            {data.pitch_list?.length > 0 && (
              <div
                className="mb-6 card-aw card-aw-lg"
                style={{ background: "var(--aw-ink)", color: "var(--aw-bg)", borderColor: "var(--aw-ink)" }}
                data-testid="citations-pitch-list"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div
                      className="eyebrow"
                      style={{ color: "oklch(0.82 0.1 155)" }}
                    >
                      <Target size={14} style={{ marginRight: 6 }} />
                      Pitch list
                    </div>
                    <div
                      className="mt-2"
                      style={{ fontFamily: "var(--font-display)", fontSize: 24 }}
                    >
                      {data.pitch_list.length} high-authority domains where you're absent
                    </div>
                    <div
                      className="mt-1 text-sm"
                      style={{ color: "#B8B3A6", maxWidth: 540 }}
                    >
                      These domains appear in AI answers about your category but have
                      never cited you. Each one is a potential PR pitch, guest post or
                      partnership.
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFilter("pitch");
                      window.scrollTo({ top: 480, behavior: "smooth" });
                    }}
                    data-testid="citations-show-pitch"
                    className="btn-aw"
                    style={{
                      background: "white",
                      color: "var(--aw-ink)",
                      borderColor: "white",
                    }}
                  >
                    View pitch list <span className="arrow">→</span>
                  </button>
                </div>
              </div>
            )}

            {/* Filter chips */}
            <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="citations-filters">
              <button
                onClick={() => setFilter("all")}
                className={`pill ${filter === "all" ? "pill-accent" : ""}`}
                style={{ cursor: "pointer", height: 28 }}
                data-testid="citations-filter-all"
              >
                All · {data.sources.length}
              </button>
              <button
                onClick={() => setFilter("pitch")}
                className={`pill ${filter === "pitch" ? "pill-accent" : ""}`}
                style={{ cursor: "pointer", height: 28 }}
                data-testid="citations-filter-pitch"
              >
                Pitch list · {data.pitch_list?.length || 0}
              </button>
              {sourceTypes.map(([t, n]) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`pill ${filter === t ? "pill-accent" : ""}`}
                  style={{ cursor: "pointer", height: 28 }}
                  data-testid={`citations-filter-${t}`}
                >
                  {SOURCE_LABEL[t] || t} · {n}
                </button>
              ))}
              <div className="flex-1" />
              <button
                onClick={exportCsv}
                className="btn-aw btn-aw-sm"
                data-testid="citations-export"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {/* Table */}
            <div
              className="card-aw overflow-x-auto"
              style={{ padding: 0 }}
              data-testid="citations-table"
            >
              <div style={{ minWidth: 680 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 80px 100px 80px",
                  padding: "12px 20px",
                  borderBottom: "1px solid var(--aw-border)",
                  background: "var(--aw-surface-2)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--aw-muted)",
                }}
              >
                <div>Domain</div>
                <div>Source</div>
                <div>Mentions</div>
                <div className="text-right">Auth.</div>
                <div className="text-right">Cites</div>
                <div></div>
              </div>
              {filtered.length === 0 ? (
                <div
                  className="text-center text-sm py-10"
                  style={{ color: "var(--aw-muted)" }}
                  data-testid="citations-empty"
                >
                  No sources match this filter.
                </div>
              ) : (
                filtered.map((s) => {
                  const isYou = s.brand_mentions > 0;
                  const isPitch = s.brand_mentions === 0;
                  const color = SOURCE_COLOR[s.source_type] || SOURCE_COLOR.other;
                  return (
                    <div
                      key={s.domain}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 80px 100px 80px",
                        padding: "14px 20px",
                        borderBottom: "1px solid var(--aw-border)",
                        alignItems: "center",
                        background: isYou ? "color-mix(in oklch, var(--aw-accent-soft) 35%, white)" : "white",
                      }}
                      data-testid={`citation-row-${s.domain}`}
                    >
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 13.5,
                          color: isYou ? "var(--aw-accent-ink)" : "var(--aw-ink-2)",
                          fontWeight: isYou ? 600 : 500,
                        }}
                      >
                        {s.domain}
                      </div>
                      <div>
                        <span
                          className="pill"
                          style={{
                            background: color.bg,
                            color: color.fg,
                            borderColor: "transparent",
                            height: 22,
                            fontSize: 11,
                          }}
                        >
                          {SOURCE_LABEL[s.source_type] || s.source_type}
                        </span>
                      </div>
                      <div className="text-sm" style={{ color: "var(--aw-ink-2)" }}>
                        {s.brand_mentions > 0 ? (
                          <span style={{ color: "var(--aw-accent-ink)", fontWeight: 600 }}>
                            You · {s.brand_mentions}
                          </span>
                        ) : Object.keys(s.competitor_mentions || {}).length ? (
                          <span style={{ color: "var(--aw-muted)" }}>
                            {Object.entries(s.competitor_mentions)
                              .slice(0, 2)
                              .map(([c, n]) => `${c} ${n}`)
                              .join(" · ")}
                          </span>
                        ) : (
                          <span style={{ color: "var(--aw-muted-2)" }}>—</span>
                        )}
                      </div>
                      <div className="mono tnum text-right text-sm">{s.authority}</div>
                      <div className="mono tnum text-right text-sm">{s.frequency}</div>
                      <div className="text-right">
                        {isPitch && (
                          <span
                            className="pill"
                            style={{
                              background: "var(--aw-surface-2)",
                              color: "var(--aw-muted)",
                              borderColor: "transparent",
                              fontSize: 10,
                              height: 20,
                            }}
                          >
                            Pitch
                          </span>
                        )}
                        <a
                          href={`https://${s.domain}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "inline-flex",
                            color: "var(--aw-muted)",
                            marginLeft: 6,
                          }}
                          data-testid={`citation-link-${s.domain}`}
                        >
                          <ExternalLink size={13} />
                        </a>
                      </div>
                    </div>
                  );
                })
              )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
